import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface CardProps {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'elevated' | 'bordered' | 'ghost';
}

export function Card({
  children,
  className,
  hover = false,
  padding = 'md',
  variant = 'default'
}: CardProps) {
  const paddings = {
    none: '',
    sm: 'p-4 sm:p-5',
    md: 'p-5 sm:p-6',
    lg: 'p-6 sm:p-8',
  };

  const variants = {
    default:
      'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800',
    elevated:
      'bg-white dark:bg-neutral-900 shadow-soft',
    bordered:
      'bg-transparent border border-neutral-200 dark:border-neutral-800',
    ghost:
      'bg-neutral-50 dark:bg-neutral-900/50',
  };

  return (
    <div
      className={cn(
        'rounded-2xl transition-all duration-300',
        variants[variant],
        hover && 'hover:shadow-soft-lg hover:border-neutral-300 dark:hover:border-neutral-700 hover:-translate-y-0.5',
        paddings[padding],
        className
      )}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div className={cn('mb-4', className)}>
      {children}
    </div>
  );
}

interface CardTitleProps {
  children: ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4';
}

export function CardTitle({ children, className, as: Component = 'h3' }: CardTitleProps) {
  return (
    <Component
      className={cn(
        'text-lg sm:text-xl font-semibold text-neutral-900 dark:text-white tracking-tight',
        className
      )}
    >
      {children}
    </Component>
  );
}

interface CardDescriptionProps {
  children: ReactNode;
  className?: string;
}

export function CardDescription({ children, className }: CardDescriptionProps) {
  return (
    <p className={cn('text-sm sm:text-base text-neutral-500 dark:text-neutral-400 leading-relaxed', className)}>
      {children}
    </p>
  );
}

interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return <div className={cn(className)}>{children}</div>;
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div className={cn('mt-5 pt-5 border-t border-neutral-100 dark:border-neutral-800', className)}>
      {children}
    </div>
  );
}

// New component for feature cards
interface FeatureCardProps {
  icon?: ReactNode;
  title: string;
  description: string;
  className?: string;
}

export function FeatureCard({ icon, title, description, className }: FeatureCardProps) {
  return (
    <Card hover className={cn('group', className)}>
      {icon && (
        <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-4 group-hover:bg-primary-500/20 transition-colors">
          <div className="text-primary-500">{icon}</div>
        </div>
      )}
      <CardTitle className="text-lg mb-2">{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </Card>
  );
}

// New component for stat cards
interface StatCardProps {
  value: string;
  label: string;
  className?: string;
}

export function StatCard({ value, label, className }: StatCardProps) {
  return (
    <div className={cn('text-center', className)}>
      <div className="text-4xl sm:text-5xl font-semibold text-neutral-900 dark:text-white tracking-tight">
        {value}
      </div>
      <div className="mt-2 text-sm sm:text-base text-neutral-500 dark:text-neutral-400">
        {label}
      </div>
    </div>
  );
}
